import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Platform, View } from 'react-native';

import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    DragEndParams,
    RenderItemParams,
} from 'react-native-draggable-flatlist';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
// eslint-disable-next-line import/no-extraneous-dependencies
import isEqual from 'lodash/isEqual';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    MY_LIST_CHANNELS_HIDDEN_BUTTON,
    MY_LIST_CHANNELS_HIDDEN_DESCRIPTION,
    MY_LIST_CHANNELS_HIDDEN_TITLE,
    SAVE_MY_LIST_BUTTON,
    SAVE_MY_LIST_CANCEL_BUTTON,
    SAVE_MY_LIST_DESCRIPTION,
    SAVE_MY_LIST_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    selectChannelsById,
    selectFavoriteChannels,
} from 'store/programs/selectors';
import { RBChannel } from 'store/railsBuilder/reducer.types.ts';
import {
    createFavouritesChannelsList,
    setLineupChanged,
    setShowLineupModal,
    updateFavouritesChannelsList,
} from 'store/user/actions';
import {
    selectEntitlements,
    selectFavouriteChannelsRequestError,
    selectShouldHideUnsubscribedChannels,
    selectShowLineupModal,
} from 'store/user/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import isSubscribedToChannel from 'utils/isSubscribedToChannel';
import { isWeb } from 'utils/responsiveness';

import {
    DIALOG_CANCEL_BUTTON,
    DIALOG_ERR_GENERIC_BODY,
    DIALOG_ERR_GENERIC_TITLE,
    DIALOG_TRY_AGAIN_BUTTON,
} from 'screens/Collection/constants';
import ChannelLineupItem, {
    ChannelsContent,
} from 'screens/Settings/components/ChannelLineupItem/ChannelLineupItem';
import DraggableList from 'screens/Settings/components/DraggableList/DraggableList';
import WebHeader from 'screens/Settings/components/WebHeader';
import useChannelsLineups from 'screens/Settings/hooks/useChannelsLineups';
import {
    BUTTON_CANCEL_CHANGES,
    BUTTON_SAVE_CHANGES,
    FTI_CHANNELS_MY_LIST_SAVE_DESCRIPTION,
    FTI_CHANNELS_MY_LIST_SAVE_TITLE,
    FTI_CHANNELS_MY_LIST_TITLE,
    MY_LIST_ALL_CHANNELS_HIDDEN_BODY,
    MY_LIST_ALL_CHANNELS_HIDDEN_TITLE,
    SCREEN_AUDIO_SUBTITLES_TITLE_GOT_IT_BUTTON,
} from 'screens/Settings/languagesConst';

import ToggleButton from 'components/CustomButtons/ToggleButton';
import DialogView from 'components/Dialog';
import ErrorDialog from 'components/ErrorDialog';
import SimpleNavigationHeaderLeft from 'components/NavigationHeader/SimpleNavigationHeader/Left';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const {
    PROFILE: { BUTTON_SAVE },
    ACCESSIBILITY: {
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        SETTINGS: {
            CHANNEL_LINEUP: {
                ACCESSIBILITY_MY_LIST_TOGGLE_ON,
                ACCESSIBILITY_MY_LIST_TOGGLE_OFF,
            },
        },
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
    },
    HIDE_UNSUBSCRIBED_CHANNELS_TOGGLE,
} = LANGUAGE_KEYS;

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.EDIT_MY_LIST>;
};

function EditMyList({ navigation }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const toggleRef = useRef<View | null>(null);

    const { background, primary } = useSelector(selectLayoutConfig);
    const channels = useSelector(selectFavoriteChannels);
    const channelsById = useSelector(selectChannelsById);
    const shoudShowLineupModal = useSelector(selectShowLineupModal);
    const entitlements = useSelector(selectEntitlements);
    const shouldHideUnsubscribedChannels = useSelector(
        selectShouldHideUnsubscribedChannels,
    );
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);
    const [hideUnsubscribedChannels, setHideUnsubscribedChannels] =
        useState<boolean>(shouldHideUnsubscribedChannels);

    const updateListError = useSelector(selectFavouriteChannelsRequestError);
    const { myList } = useChannelsLineups();

    const initialChannelsArray = useMemo<ChannelsContent[]>(() => {
        if (myList?.metadata) {
            return Object.values(myList.metadata).map((m) => {
                const metadata = JSON.parse(m);
                const channel: RBChannel | undefined =
                    channelsById[metadata.channelId];

                return {
                    editorial: {
                        id: channel?.editorial?.id || metadata.channelId,
                        longName: channel?.editorial?.longName || '',
                    },
                    hidden: !channel,
                    visible:
                        channel && shouldHideUnsubscribedChannels
                            ? isSubscribedToChannel(channel, entitlements) &&
                              !metadata.isHidden
                            : !metadata.isHidden,
                    isSubscribed:
                        !!channel &&
                        isSubscribedToChannel(channel, entitlements),
                };
            });
        }

        return channels.map(({ editorial: { id, longName } }, index) => ({
            editorial: {
                id,
                longName,
            },
            hidden: false,
            visible: true,
            isSubscribed: isSubscribedToChannel(channels[index], entitlements),
        }));
    }, [
        myList?.metadata,
        channels,
        channelsById,
        entitlements,
        shouldHideUnsubscribedChannels,
    ]);

    const [listToEdit, setListToEdit] =
        useState<ChannelsContent[]>(initialChannelsArray);

    const [showModalDialog, setShowModalDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const areChannelsHidden = useMemo(
        () => listToEdit.every(({ visible }) => !visible),
        [listToEdit],
    );

    const handleCloseModal = useCallback(() => {
        setShowModalDialog(false);
    }, []);

    const handleOpenModal = useCallback(() => {
        setShowModalDialog(true);
    }, []);

    const handleListDataUpdate = useCallback(
        ({ data }: DragEndParams<ChannelsContent>) => {
            setListToEdit(data);
            dispatch(setLineupChanged(true));
        },
        [],
    );

    const toggleChannelVisibility = useCallback(
        (index: number) => {
            dispatch(setLineupChanged(true));

            setListToEdit((channelsList) => {
                const updatedChannelsList = [...channelsList];
                updatedChannelsList[index] = {
                    ...updatedChannelsList[index],
                    visible: !updatedChannelsList[index].visible,
                };

                if (
                    !updatedChannelsList[index].isSubscribed &&
                    !!updatedChannelsList[index].visible
                ) {
                    setHideUnsubscribedChannels(false);
                }

                return updatedChannelsList;
            });
        },
        [listToEdit],
    );

    const handleUpdateSuccess = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.reset({
                routes: [
                    {
                        name: myList ? SCREEN.MY_LIST : SCREEN.CHANNELS_LINEUP,
                    },
                ],
            });
        }

        setTimeout(() => {
            dispatch(setLineupChanged(false));
            dispatch(setShowLineupModal(false));
        }, 300);
    }, [myList]);

    const handleOpenErrorDialog = useCallback(() => {
        setShowModalDialog(false);
        setShowErrorDialog(true);
    }, []);

    const handleListUpdate = useCallback(() => {
        const updatedMyList = listToEdit.reduce<Record<string, string>>(
            (acc, { editorial: { id }, visible, isSubscribed }, index) => ({
                ...acc,
                [`${index + 1}`]: JSON.stringify({
                    channelId: id,
                    // If the channel is not subscribed, it should be visible to ensure it is not hidden when entitlements change
                    isHidden: hideUnsubscribedChannels
                        ? isSubscribed && !visible
                        : !visible,
                    sortIndex: index + 1,
                }),
            }),
            {},
        );

        const meta = {
            onSuccess: handleUpdateSuccess,
            onFailure: handleOpenErrorDialog,
        };

        if (myList) {
            dispatch(
                updateFavouritesChannelsList(
                    {
                        ...myList,
                        metadata: updatedMyList,
                        tags: hideUnsubscribedChannels
                            ? ['active', 'unsubscribed_hidden']
                            : ['active'],
                    },
                    meta,
                ),
            );
        } else {
            dispatch(
                createFavouritesChannelsList(
                    updatedMyList,
                    meta,
                    hideUnsubscribedChannels
                        ? ['active', 'unsubscribed_hidden']
                        : ['active'],
                ),
            );
        }
    }, [
        listToEdit,
        myList,
        hideUnsubscribedChannels,
        dispatch,
        handleUpdateSuccess,
        handleOpenErrorDialog,
    ]);

    const handleCloseErrorDialog = useCallback(() => {
        setShowErrorDialog(false);
    }, []);

    const handleCheckListChange = useCallback(() => {
        // Check if the hide unsubscribed channels toggle has changed
        if (hideUnsubscribedChannels !== shouldHideUnsubscribedChannels) {
            return true;
        }

        return !isEqual(listToEdit, initialChannelsArray);
    }, [
        hideUnsubscribedChannels,
        shouldHideUnsubscribedChannels,
        listToEdit,
        initialChannelsArray,
    ]);

    const handleHardwareBackPress = useCallback(() => {
        const wasListChanged = handleCheckListChange();

        if (wasListChanged) {
            handleOpenModal();
            return true;
        }

        dispatch(setLineupChanged(false));
        navigation.pop();
        return true;
    }, [handleOpenModal, handleCheckListChange, navigation]);

    const errorContent = useMemo(() => {
        if (areChannelsHidden) {
            return {
                title: MY_LIST_ALL_CHANNELS_HIDDEN_TITLE,
                titleTestID: MY_LIST_CHANNELS_HIDDEN_TITLE,
                description: MY_LIST_ALL_CHANNELS_HIDDEN_BODY,
                descriptionTestID: MY_LIST_CHANNELS_HIDDEN_DESCRIPTION,
                cancelButton: SCREEN_AUDIO_SUBTITLES_TITLE_GOT_IT_BUTTON,
                cancelButtonTestID: MY_LIST_CHANNELS_HIDDEN_BUTTON,
                cancelButtonAction: handleCloseModal,
            };
        }

        return {
            title: FTI_CHANNELS_MY_LIST_SAVE_TITLE,
            titleTestID: SAVE_MY_LIST_TITLE,
            description: FTI_CHANNELS_MY_LIST_SAVE_DESCRIPTION,
            descriptionTestID: SAVE_MY_LIST_DESCRIPTION,
            cancelButton: BUTTON_SAVE_CHANGES,
            cancelButtonTestID: SAVE_MY_LIST_BUTTON,
            cancelButtonAction: handleListUpdate,
            activeButton: BUTTON_CANCEL_CHANGES,
            activeButtonTestID: SAVE_MY_LIST_CANCEL_BUTTON,
        };
    }, [areChannelsHidden, handleCloseModal, handleListUpdate]);

    const accessibilityLabel = useMemo(
        () =>
            `${t(BUTTON_SAVE)} ${
                isWeb ? t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB) : ''
            }`,
        [t],
    );

    const renderActionButton = useCallback(
        () => (
            <TouchableOpacity
                onPress={handleListUpdate}
                style={styles.actionButton}
                accessibilityLabel={accessibilityLabel}
                accessibilityRole="button"
                {...(Platform.OS === 'ios' && {
                    accessibilityHint: t(ACCESSIBILITY_GENERAL_TAPP_TWICE),
                })}
                accessible
            >
                <Text
                    color={ETextColor.neutral}
                    category={ETextCategory.body1}
                    style={styles.saveButton}
                >
                    {t(BUTTON_SAVE)}
                </Text>
            </TouchableOpacity>
        ),
        [handleListUpdate, accessibilityLabel, t],
    );

    const handleToggleSwitch = useCallback(() => {
        const updatedChannelsList = listToEdit.map((channel) => ({
            ...channel,
            visible: channel.isSubscribed
                ? channel.visible
                : hideUnsubscribedChannels,
        }));

        setHideUnsubscribedChannels((prev) => !prev);
        dispatch(setLineupChanged(true));
        setListToEdit(updatedChannelsList);
    }, [listToEdit, hideUnsubscribedChannels, dispatch]);

    const renderBackButton = useCallback(
        () => <SimpleNavigationHeaderLeft onPress={handleHardwareBackPress} />,
        [t, handleHardwareBackPress],
    );

    const renderItem = useCallback(
        ({ item, drag, getIndex }: RenderItemParams<ChannelsContent>) =>
            item.hidden ? (
                <View />
            ) : (
                <ChannelLineupItem
                    onLongPress={drag}
                    item={item}
                    toggleItemVisibility={toggleChannelVisibility}
                    index={getIndex()}
                    screenReaderEnabled={screenReaderEnabled}
                />
            ),
        [toggleChannelVisibility, screenReaderEnabled],
    );

    useHeaderConfig({
        isRoot: isWeb,
        disableFocusOnMount: true,
        headerRight: renderActionButton,
        headerLeft: renderBackButton,
    });

    useEffect(() => {
        const focusTimeout = setTimeout(
            () => setAccessibilityFocusTo(toggleRef),
            300,
        );

        return () => clearTimeout(focusTimeout);
    }, [toggleRef]);

    useEffect(() => {
        if (updateListError) {
            setShowErrorDialog(true);
        }
    }, [updateListError]);

    useBackHandler(handleHardwareBackPress);

    useEffect(() => {
        setShowModalDialog(shoudShowLineupModal);
    }, [shoudShowLineupModal]);

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(FTI_CHANNELS_MY_LIST_TITLE)}
                    renderActionButton={renderActionButton}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    {
                        backgroundColor: background,
                    },
                ]}
                dataSet={{ class: 'myListContainer' }}
            >
                <View style={isWeb && styles.stickyContainerForWeb}>
                    <View
                        ref={toggleRef}
                        style={styles.switchContainer}
                        accessible={Platform.OS !== 'ios'}
                        {...(isWeb && { tabIndex: 0 })}
                    >
                        <Text style={styles.headerText}>
                            {t(HIDE_UNSUBSCRIBED_CHANNELS_TOGGLE)}
                        </Text>
                        <ToggleButton
                            value={hideUnsubscribedChannels}
                            onValueChange={handleToggleSwitch}
                            accessibilityLabelWhenToggledOff={t(
                                ACCESSIBILITY_MY_LIST_TOGGLE_OFF,
                            )}
                            accessibilityLabelWhenToggledOn={t(
                                ACCESSIBILITY_MY_LIST_TOGGLE_ON,
                            )}
                        />
                    </View>

                    <View style={styles.line} />
                </View>
                <View style={styles.channelListWrapper}>
                    <DraggableList
                        data={listToEdit}
                        renderItem={renderItem}
                        onDragEnd={handleListDataUpdate}
                        toggleChannelVisibility={toggleChannelVisibility}
                    />
                </View>
            </View>

            <DialogView
                visible={showModalDialog}
                style={[styles.dialog, { backgroundColor: background }]}
                title={errorContent.title}
                titleTestID={errorContent.titleTestID}
                descriptionNumberOfLines={isWeb ? 5 : 6}
                description={errorContent.description}
                descriptionTestID={errorContent.descriptionTestID}
                actionButtonTitle={errorContent?.activeButton}
                actionButtonFillColor={background}
                cancelButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                actionButtonTestID={errorContent?.activeButtonTestID}
                cancelButtonTitle={errorContent?.cancelButton}
                cancelButtonFillColor={primary}
                closeButtonTestID={errorContent.cancelButtonTestID}
                actionButtonOnPress={handleUpdateSuccess}
                cancelButtonOnPress={errorContent.cancelButtonAction}
                actionButtonBordered
                cancelButtonBordered
                revertButtons
            />

            <ErrorDialog
                visible={showErrorDialog}
                animationType="fade"
                title={DIALOG_ERR_GENERIC_TITLE}
                description={DIALOG_ERR_GENERIC_BODY}
                actionButtonTitle={DIALOG_CANCEL_BUTTON}
                actionButtonOnPress={handleCloseErrorDialog}
                errorCode={`${updateListError || ''}`}
                cancelButtonTitle={DIALOG_TRY_AGAIN_BUTTON}
                cancelButtonFillColor={primary}
                cancelButtonStyle={[styles.button, { borderColor: primary }]}
                actionButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                cancelButtonOnPress={handleListUpdate}
                actionButtonBordered
                revertButtons
            />
        </>
    );
}

export default EditMyList;
